import { AxiosError } from 'axios';
import { validate } from 'uuid';
/**
 * The default options for circuit breaker.
 */
export var DEFAULT_OPTIONS = {
  // Defines number of requests (thresholdInError) that failed in thresholdExpireInMs period,
  // which will cause Circuit Breaker to be OPEN state. Only applied for 5XX error.
  thresholdInError: 3,
  thresholdExpireInMs: 5000,
  // Defines number of requests that successfully completed in HALF_OPEN state
  // to switch Circuit Breaker to CLOSED state
  thresholdToCloseState: 3,
  // Sustain OPEN state time before switch from OPEN to HALF_OPEN state
  openSustainTimeInMs: 20000,
  // Number of request per minutes (rpm) to calculate the rate limit.
  enableRPMControl: false,
  requestPerMinute: 0 // Use 0 as unlimited rate limit by default, set to reasonable value on demand
};

/**
 * The default retry after time in seconds.
 */
export var DEFAULT_RETRY_AFTER_IN_SECOND = 60;
var convertItem = function convertItem(item) {
  // project uid or other resource uid
  if (validate(item)) return ':uid';
  // resource id
  if (/^\d+$/.test(item)) return ':id';
  // Folder urn
  if (item.startsWith('urn:') || item.startsWith('urn%3A')) return ':urn';
  // Check if is a long encoded string, which maybe a thumbnail urn or others.
  // E.g. /derivativeservice/v2/thumbnails/dxjuomfkc2sud2lwcwe6znmuzmlsztp2zi55c2flcxc4afrccxdybncwrm9prk9np3zlcnnpb249mq
  if (!/[\.\-\:]/.test(item) && item.length >= 30) return ':hash';
  // Check mixed with uuid e.g. 11a2bf8b-87e1-44cf-99d4-301a33af6590.zip
  if (item.indexOf('.') !== -1) {
    var containUid = item.split('.').some(function (subItem) {
      return validate(subItem);
    });
    if (containUid) return ':uid';
  }
  return item;
};

/**
 * Get the API request url pattern for circuit breaker key.
 *
 * @param param0 - The request config
 * @param param1 - The breaker options
 *
 * @returns The api key user by circuit breaker
 */
export var getKey = function getKey(_ref) {
  var _ref$url = _ref.url,
    url = _ref$url === void 0 ? '' : _ref$url,
    _ref$baseURL = _ref.baseURL,
    baseURL = _ref$baseURL === void 0 ? '' : _ref$baseURL,
    _ref$method = _ref.method,
    method = _ref$method === void 0 ? '' : _ref$method;
  var _ref2 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    _ref2$monitorPerUrl = _ref2.monitorPerUrl,
    monitorPerUrl = _ref2$monitorPerUrl === void 0 ? true : _ref2$monitorPerUrl,
    _ref2$disabled = _ref2.disabled,
    disabled = _ref2$disabled === void 0 ? false : _ref2$disabled,
    _ref2$splitter = _ref2.splitter,
    splitter = _ref2$splitter === void 0 ? '/' : _ref2$splitter;
  // If current axios instance is disabled the circuit breaker, return null directly
  if (disabled) return null;
  // If the circuit breaker is enabled, but not monitor per url, enable it base on baseUrl and ignore the method
  if (!monitorPerUrl) return !baseURL ? null : baseURL.toLowerCase();

  // Base on url pattern: (METHOD) + (BASE_URL) + PATH
  var index = url.indexOf('?'); // Ignore query string
  var path = index !== -1 ? url.substring(0, index) : url;
  if (!path) return null;
  var pattern = "".concat(baseURL).concat(path).split('/').filter(Boolean).map(function (item) {
    return convertItem(item);
  }).join('/').toLowerCase();
  return "".concat(method).concat(splitter).concat(pattern);
};

/**
 * Get the retry after time in seconds.
 *
 * @param retryHeader - the retry header
 *
 * @returns The retry after time in seconds
 */
export var getRetryAfter = function getRetryAfter(retryHeader) {
  var retryAfter = DEFAULT_RETRY_AFTER_IN_SECOND;
  if (retryHeader) {
    if (retryHeader.constructor === Number) {
      retryAfter = retryHeader.valueOf();
    } else if (/^\d+$/.test(retryHeader)) {
      retryAfter = parseInt(retryHeader);
    } else {
      // test if the header is a date
      var date = Date.parse(retryHeader);
      if (!isNaN(date) && date > Date.now()) {
        retryAfter = (date - Date.now()) / 1000;
      }
    }
  }
  return Math.min(retryAfter, 120); // max 2 minutes
};

/**
 * Generate an axios error with message, code, config and response.
 *
 * @param message - the error message
 * @param code - the error code
 * @param config - the request config
 * @param res - the response
 * @returns The axios error
 */
export var genAxiosError = function genAxiosError(message, code, config, res) {
  var error = new AxiosError(message);
  error.config = config;
  error.code = code;
  error.response = res;
  return error;
};